import type React from 'react';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BasePage from '@/components/layout/BasePage';

import apiService, { ApiError } from '@/services/apiService';

interface ForwarderProps {
	/**
	 * Route base to land on after the token is stored (e.g. `/aanbod` for the
	 * quote flow, `/dossier` for the read-only request overview). The form id is
	 * appended. Defaults to the quote page.
	 */
	destination?: string;
}

const Quote = ({ destination = '/aanbod' }: ForwarderProps): React.JSX.Element => {
	const navigate = useNavigate();
	const { id, token } = useParams<{ id: string; token: string }>();

	useEffect(() => {
		if (!id || !token) return;

		const target = `${destination}/${id}`;
		const previousToken = localStorage.getItem(id);
		localStorage.setItem(id, token);
		(async () => {
			try {
				const longLivedToken = await apiService.token.refresh(id);
				localStorage.setItem(id, longLivedToken.access_token);
				navigate(target, { replace: true });
			} catch (err) {
				if (previousToken) {
					localStorage.setItem(id, previousToken);
				} else {
					localStorage.removeItem(id);
				}
				const isExpiredToken = err instanceof ApiError && err.code === 'token_expired';
				if (isExpiredToken) {
					navigate(`/link-verlopen/${id}?token=${encodeURIComponent(token)}`, { replace: true });
				} else {
					navigate(target, { replace: true });
				}
			}
		})();
	}, [token, navigate, id, destination]);

	return <BasePage isLoading={true} />;
};

export default Quote;
